import { Component, Input, Output, OnDestroy, OnInit, ViewChild, EventEmitter } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { filter, mergeMap, take, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import moment from 'moment-timezone';
import { ImageCroppedEvent } from 'ngx-image-cropper';
import produce from 'immer';
import { ProfilePreferences } from '../../store/notification-prefs.model';

@Component({
  selector: 'topo-edit-profile',
  templateUrl: './edit-profile.component.html',
  styleUrls: ['./edit-profile.component.scss'],
  standalone: false
})
export class EditProfileComponent implements OnInit, OnDestroy {
  @ViewChild('photo') photo;
  @Input() customPlaceholderText = 'Choose...';
  @Input() customNotFoundText = 'No zone found';
  @Input() virtualScroll = true;
  @Output() complete = new EventEmitter<ProfilePreferences>();
  @Output() close = new EventEmitter<void>();
  imageUrl: string;
  form: FormGroup;
  $destroy = new Subject<void>();
  isActive$: Subject<boolean> = new Subject<boolean>();
  supportedUnits = ['MILES', 'KILOMETERS'];
  profileLabel: string;
  profileLabelColor: string;
  colors = ['#4782B6', '#FBE7E8', '#FFBA08', '#3A8E06', '#EE6400', '#6747E9'];
  croppedImage = '';
  showCropper = false;
  isEditingPhoto = false;
  imageFile: any = null;

  @Input()
  set active(value: boolean) {
    this.isActive$.next(value);
  }

  @Input()
  set profilePreferences(value: ProfilePreferences) {
    if (!value) {
      return;
    }

    if (!this.form) {
      this.initForm();
    }

    this.form.patchValue(value);

    this.croppedImage = value.avatar_image;

    let distance_unit = value.distance_unit;
    if (!distance_unit || distance_unit.length === 0) {
      distance_unit = 'miles';
      this.form.patchValue({ distance_unit });
    }
  }

  constructor(private fb: FormBuilder) {}

  ngOnDestroy(): void {
    this.$destroy.next();
    this.$destroy.complete();
  }

  ngOnInit(): void {
    this.initForm();
  }

  initForm(): void {
    this.form = this.fb.group({
      display_name: [],
      distance_unit: [],
      use_24h_time: [],
      avatar_image: []
    });
  }

  changePhoto(): void {
    this.photo.nativeElement.click();
  }

  fileChangeEvent(event: any): void {
    const file = event.target.files[0];
    this.imageFile = file;
    this.isEditingPhoto = true;
  }

  imageCropped(event: ImageCroppedEvent): void {
    this.croppedImage = event.base64;
    this.form.patchValue({ avatar_image: event.base64 });
  }

  imageLoaded(): void {
    this.showCropper = true;
  }

  cropperReady(): void {}

  loadImageFailed(): void {}

  clearPhoto(): void {
    this.form.patchValue({ avatar_image: null });
  }

  closeImageCropper(): void {
    this.showCropper = false;
    this.isEditingPhoto = false;
    this.imageFile = null;
    this.croppedImage = null;
  }

  updatePhoto(): void {
    this.form.patchValue({ avatar_image: this.croppedImage });
    this.closeImageCropper();
  }

  doneClick(): void {
    this.complete.emit(this.form.value);
  }

  cancelClick(): void {
    this.close.emit();
  }

  getInitials(): string {
    let value = 'AA';

    const name = this.form.get('display_name').value;

    if (!!name) {
      const segments = name.split(' ');
      if (segments.length > 1) {
        value = `${segments[0][0]}${segments[1][0]}`;
      } else {
        value = name.length > 1 ? name.substring(0, 2) : name;
      }
    }

    value = value.toLocaleUpperCase();

    return value;
  }

  getBackgroundColor(initials: string): string {
    if (initials) {
      const code = initials.charCodeAt(0);
      const index = code % 6;

      return this.colors[index];
    }

    return this.colors[0];
  }
}
